import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CloudRain, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { inr } from "@/lib/format";
import { resetRainSettings } from "@/components/OrderBreakdownRows";

export const RainSurchargeSettings = ({ canEdit }) => {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/settings").then(({ data }) => setF({ rain_enabled: data.rain_enabled !== false, rain_amount: data.rain_amount ?? 20, rain_max_orders: data.rain_max_orders ?? 100 }));
  }, []);
  if (!f) return null;
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", { rain_enabled: f.rain_enabled, rain_amount: Number(f.rain_amount), rain_max_orders: Number(f.rain_max_orders) });
      resetRainSettings();
      toast.success("Rain surcharge saved. New entries use it; saved entries keep their rate.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    setBusy(false);
  };
  return (
    <div className="max-w-2xl space-y-4 rounded-xl border border-border bg-card p-5" data-testid="rain-settings">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-500/15 text-sky-500"><CloudRain className="h-5 w-5" /></div>
          <div><p className="font-head text-sm font-semibold">Rain Surcharge</p><p className="text-xs text-muted-foreground">Paid per completed order delivered in rain</p></div>
        </div>
        <Switch checked={f.rain_enabled} disabled={!canEdit} onCheckedChange={(v) => setF({ ...f, rain_enabled: v })} data-testid="rain-enabled-switch" />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div><Label>Surcharge per Rain Order (₹)</Label><Input type="number" min="0" max="1000" disabled={!canEdit} value={f.rain_amount} onChange={(e) => setF({ ...f, rain_amount: e.target.value })} data-testid="rain-amount" /></div>
        <div><Label>Maximum Rain Orders per Day</Label><Input type="number" min="1" max="100" disabled={!canEdit} value={f.rain_max_orders} onChange={(e) => setF({ ...f, rain_max_orders: e.target.value })} data-testid="rain-max-orders" /></div>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="rain-example">Example: 15 rain orders × {inr(Number(f.rain_amount || 0))} = <b className="text-foreground">{f.rain_enabled ? inr(15 * Number(f.rain_amount || 0)) : inr(0)}</b>. Custom earning models with their own Rain Bonus use that amount per rain order. Changes apply to new entries only; saved entries keep the rate they were saved with.</p>
      {canEdit ? <Button onClick={save} disabled={busy} data-testid="rain-settings-save"><Save className="mr-1.5 h-4 w-4" />Save Rain Surcharge</Button>
        : <p className="text-xs text-amber-500">You can view these settings but not change them.</p>}
    </div>
  );
};
